import type { ReportPreview } from './api';

const KEY_PREFIX = 'x5admin.report-preview.';

export const PREVIEW_WINDOW_PATH = '/reporting/preview';

function isPreview(value: unknown): value is ReportPreview {
  if (!value || typeof value !== 'object') return false;
  const v = value as Partial<ReportPreview>;
  return typeof v.html === 'string' && typeof v.report_id === 'string';
}

/**
 * Hands a rendered preview to a second tab through sessionStorage, which the
 * browser clones into a window opened with an opener. That saves re-running the
 * Athena query, and unlike localStorage the copy dies with the tab instead of
 * leaving report rows on disk.
 */
export function openPreviewWindow(preview: ReportPreview): void {
  const token = preview.query_execution_id;
  try {
    for (const key of Object.keys(window.sessionStorage)) {
      if (key.startsWith(KEY_PREFIX)) window.sessionStorage.removeItem(key);
    }
    window.sessionStorage.setItem(KEY_PREFIX + token, JSON.stringify(preview));
  } catch {
    // Quota or disabled storage: the window falls back to offering a re-run.
  }

  const params = new URLSearchParams({
    report: preview.report_id,
    date: preview.target_date,
    k: token,
  });
  // No `noopener` — without an opener the browser makes no sessionStorage copy.
  window.open(`${PREVIEW_WINDOW_PATH}?${params.toString()}`, '_blank');
}

export function readStashedPreview(token: string): ReportPreview | null {
  try {
    const raw = window.sessionStorage.getItem(KEY_PREFIX + token);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isPreview(parsed) ? parsed : null;
  } catch {
    return null;
  }
}
